<script setup lang="ts">
import { useControlId } from '../../composables/useControlId'
import {
  inject,
  computed,
  ref,
  watch,
  onMounted,
  onUnmounted,
  provide,
  type Ref,
  type CSSProperties
} from 'vue'
import { CircleHelp } from '@lucide/vue'
import RrTooltip from '../tooltip/RrTooltip.vue'
import type { FormContext, FormItemContext, NamePath, Rule, RuleObject } from './types'
import {
  normalizeNamePath,
  getPathValue,
  setPathValue,
  cloneDeepValue,
  matchesTrigger,
  executeFieldValidation
} from './validator'

const props = withDefaults(
  defineProps<{
    label?: string
    htmlFor?: string
    name?: NamePath
    rules?: Rule | Rule[]
    required?: boolean
    help?: string
    extra?: string
    tooltip?: string | { title?: string; trigger?: 'hover' | 'click' | ('hover' | 'click')[] }
    validateStatus?: 'success' | 'warning' | 'error' | 'validating'
    labelCol?: { span?: number; style?: CSSProperties | Record<string, string | number> }
    wrapperCol?: { span?: number; style?: CSSProperties | Record<string, string | number> }
    colon?: boolean
    validateFirst?: boolean
    validateTrigger?: string | string[]
    autoLink?: boolean
    hasFeedback?: boolean
  }>(),
  {
    required: undefined,
    colon: undefined,
    autoLink: true,
    hasFeedback: false
  }
)

const formContext = inject<Ref<FormContext> | null>('rrFormContext', null)
const itemEl = ref<HTMLElement | null>(null)
const fieldId = useControlId()
const controlId = computed(() => props.htmlFor ?? `rr-field-${fieldId}`)
const helpId = computed(() => mergedHelp.value ? `${controlId.value}-help` : undefined)

const innerValidateStatus = ref<'success' | 'warning' | 'error' | 'validating' | undefined>(undefined)
const innerHelp = ref<string>('')

const normalizedName = computed(() => normalizeNamePath(props.name))

const mergedLayout = computed(() => formContext?.value?.layout ?? 'horizontal')
const mergedLabelAlign = computed(() => formContext?.value?.labelAlign ?? 'right')
const hideRequiredMark = computed(() => formContext?.value?.hideRequiredMark ?? false)

const mergedRules = computed<RuleObject[]>(() => {
  const formRule = getPathValue(formContext?.value?.rules, normalizedName.value)
  const source = props.rules ?? formRule ?? []
  const result: RuleObject[] = Array.isArray(source) ? [...source] : [source]
  if (props.required !== undefined && !result.some(rule => rule.required !== undefined)) {
    result.unshift({ required: props.required, message: `${props.label || '此项'}为必填项` })
  }
  const trigger = props.validateTrigger ?? formContext?.value?.validateTrigger ?? 'change'
  return result.map(rule => ({ ...rule, trigger: rule.trigger ?? trigger }))
})

const isRequired = computed(() => {
  if (props.required) return true
  return mergedRules.value.some(r => Boolean(r.required))
})

const showColon = computed(() => {
  if (props.colon !== undefined) return props.colon
  return mergedLayout.value === 'horizontal'
})

const tooltipTitle = computed(() => {
  if (typeof props.tooltip === 'string') return props.tooltip
  if (typeof props.tooltip === 'object' && props.tooltip !== null) {
    return props.tooltip.title || ''
  }
  return ''
})

const tooltipTrigger = computed<('hover' | 'click')[] | 'hover' | 'click'>(() => {
  if (typeof props.tooltip === 'object' && props.tooltip !== null && props.tooltip.trigger) {
    return props.tooltip.trigger
  }
  return ['hover', 'click']
})

const labelStyle = computed(() => {
  const col = props.labelCol ?? formContext?.value?.labelCol
  if (!col) return {}
  if (col.style) return col.style
  if (typeof col.span === 'number') {
    return { width: `${(col.span / 24) * 100}%`, flex: `0 0 ${(col.span / 24) * 100}%` }
  }
  return {}
})

const wrapperStyle = computed(() => {
  const col = props.wrapperCol ?? formContext?.value?.wrapperCol
  if (!col) return { flex: '1 1 0%', minWidth: 0 }
  if (col.style) return col.style
  if (typeof col.span === 'number') {
    return { width: `${(col.span / 24) * 100}%`, flex: `0 0 ${(col.span / 24) * 100}%` }
  }
  return { flex: '1 1 0%', minWidth: 0 }
})

const fieldValue = computed(() => {
  if (!formContext?.value?.model || normalizedName.value.length === 0) return undefined
  return getPathValue(formContext.value.model, normalizedName.value)
})

const mergedValidateStatus = computed(() => props.validateStatus ?? innerValidateStatus.value)
const mergedHelp = computed(() => props.help ?? innerHelp.value)

let initialValue: any = undefined
let validationVersion = 0
let unregister: (() => void) | null = null

async function validate(trigger?: string): Promise<string[]> {
  if (trigger && !mergedRules.value.some(rule => matchesTrigger(rule.trigger, trigger))) return []
  const version = ++validationVersion
  innerValidateStatus.value = mergedRules.value.length ? 'validating' : undefined
  const errors = await executeFieldValidation(fieldValue.value, mergedRules.value, normalizedName.value.join('.'), props.label, trigger, props.validateFirst)
  if (version === validationVersion) {
    innerValidateStatus.value = errors.length ? 'error' : mergedRules.value.length ? 'success' : undefined
    innerHelp.value = errors[0] ?? ''
  }
  return errors
}

function resetField(): void {
  if (formContext?.value?.model && normalizedName.value.length > 0) {
    setPathValue(formContext.value.model, normalizedName.value, cloneDeepValue(initialValue))
  }
  clearValidate()
}

function clearValidate(): void {
  validationVersion++
  innerValidateStatus.value = undefined
  innerHelp.value = ''
}

function setErrors(errors: string[]): void {
  validationVersion++
  if (errors && errors.length > 0) {
    innerValidateStatus.value = 'error'
    innerHelp.value = errors[0]
  } else {
    innerValidateStatus.value = undefined
    innerHelp.value = ''
  }
}

watch(fieldValue, () => { validationVersion++ }, { deep: true, flush: 'sync' })

onMounted(() => {
  if (formContext?.value?.model && normalizedName.value.length > 0) {
    initialValue = cloneDeepValue(fieldValue.value)
  }

  if (formContext?.value?.registerField) {
    unregister = formContext.value.registerField({
      get name() { return normalizedName.value },
      validate,
      resetField,
      clearValidate,
      setErrors,
      getEl: () => itemEl.value
    })
  }
})

onUnmounted(() => {
  validationVersion++
  if (unregister) {
    unregister()
    unregister = null
  }
})

provide<FormItemContext>('rrFormItemContext', {
  id: controlId,
  helpId,
  validateStatus: mergedValidateStatus,
  hasFeedback: props.hasFeedback,
  onFieldBlur: () => props.autoLink ? validate('blur') : undefined,
  onFieldChange: () => props.autoLink ? validate('change') : undefined
})

defineExpose({
  onFieldBlur: () => validate('blur'),
  onFieldChange: () => validate('change'),
  validate,
  resetField,
  clearValidate,
  getEl: () => itemEl.value
})
</script>

<template>
  <div
    ref="itemEl"
    class="rr-form-item"
    :class="[
      `rr-form-item--${mergedLayout}`,
      `rr-form-item--label-${mergedLabelAlign}`,
      {
        'has-error': mergedValidateStatus === 'error',
        'has-warning': mergedValidateStatus === 'warning',
        'has-success': mergedValidateStatus === 'success',
        'is-validating': mergedValidateStatus === 'validating'
      }
    ]"
  >
    <div
      v-if="label || $slots.label"
      class="rr-form-item__label"
      :style="labelStyle"
    >
      <label :for="controlId" class="rr-form-item__label-text" :class="{ 'is-required': isRequired && !hideRequiredMark }">
        <slot name="label">{{ label }}</slot>
        <span v-if="tooltip || $slots.tooltip" class="rr-form-item__tooltip">
          <RrTooltip :title="tooltipTitle" :trigger="tooltipTrigger" placement="top">
            <span class="rr-form-item__tooltip-icon" role="button" tabindex="0" aria-label="提示信息">
              <CircleHelp :size="13" aria-hidden="true" />
            </span>
            <template v-if="$slots.tooltip" #title>
              <slot name="tooltip" />
            </template>
          </RrTooltip>
        </span>
        <span v-if="showColon" class="rr-form-item__colon">:</span>
      </label>
    </div>

    <div class="rr-form-item__control" :style="wrapperStyle">
      <div class="rr-form-item__control-input">
        <slot />
      </div>
      <div v-if="mergedHelp || $slots.help" class="rr-form-item__explain" :id="helpId" aria-live="polite">
        <slot name="help">{{ mergedHelp }}</slot>
      </div>
      <div v-if="extra || $slots.extra" class="rr-form-item__extra">
        <slot name="extra">{{ extra }}</slot>
      </div>
    </div>
  </div>
</template>

<style scoped>
.rr-form-item {
  display: flex;
  margin-bottom: 0;
  box-sizing: border-box;
}

.rr-form-item--horizontal {
  flex-direction: row;
  align-items: flex-start;
  gap: var(--rr-space-3, 12px);
}

.rr-form-item--vertical {
  flex-direction: column;
  align-items: stretch;
  gap: var(--rr-space-1, 4px);
}

.rr-form-item--inline {
  flex-direction: row;
  align-items: center;
  gap: var(--rr-space-2, 8px);
}

.rr-form-item__label {
  display: flex;
  align-items: center;
  min-height: 28px;
  color: var(--rr-color-text-strong);
  font-size: var(--rr-font-size-base, 13px);
  font-weight: var(--rr-font-weight-medium, 500);
  box-sizing: border-box;
}

.rr-form-item--label-right .rr-form-item__label {
  justify-content: flex-end;
}

.rr-form-item--label-left .rr-form-item__label,
.rr-form-item--vertical .rr-form-item__label {
  justify-content: flex-start !important;
}

.rr-form-item__label-text {
  display: inline-flex;
  align-items: center;
}

.rr-form-item__label-text.is-required::before {
  content: '*';
  display: inline-block;
  margin-right: 4px;
  color: var(--rr-color-danger, #be3038);
  font-size: 14px;
  font-family: SimSun, sans-serif;
  line-height: 1;
}

.rr-form-item__colon {
  margin-left: 2px;
  margin-right: 8px;
  position: relative;
  top: -0.5px;
}

.rr-form-item__tooltip {
  display: inline-flex;
  align-items: center;
  margin-left: 4px;
  color: var(--rr-color-text-subtle, #8a94a2);
  vertical-align: middle;
  line-height: 1;
  transition: color var(--rr-motion-duration-fast, 150ms) ease;
}

.rr-form-item__tooltip:hover {
  color: var(--rr-color-text-strong, #2b3038);
}

.rr-form-item__tooltip-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
}

.rr-form-item__control {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.rr-form-item__control-input {
  display: flex;
  align-items: center;
  min-height: 32px;
  width: 100%;
}

.rr-form-item__explain {
  font-size: var(--rr-font-size-sm, 12px);
  line-height: 1.4;
  margin-top: 4px;
  color: var(--rr-color-text-muted);
}

.has-error .rr-form-item__explain {
  color: var(--rr-color-danger, #be3038);
}

.has-warning .rr-form-item__explain {
  color: var(--rr-color-warning, #946008);
}

.has-success .rr-form-item__explain {
  color: var(--rr-color-success, #157a55);
}

.rr-form-item__extra {
  font-size: var(--rr-font-size-sm, 12px);
  line-height: 1.4;
  margin-top: 4px;
  color: var(--rr-color-text-subtle);
}

/* 控件联动高亮样式：确保在 has-error 下输入框与选择器自动展示 Danger 警告边框与阴影 */
.has-error :deep(.rr-input-wrapper),
.has-error :deep(.rr-select-selector),
.has-error :deep(.rr-textarea-wrapper) {
  border-color: var(--rr-color-danger, #be3038) !important;
}

.has-error :deep(.rr-input-wrapper.is-focused),
.has-error :deep(.rr-select-selector.is-focused),
.has-error :deep(.rr-textarea-wrapper.is-focused) {
  box-shadow: 0 0 0 3px var(--rr-color-danger-soft, #fff0f1) !important;
}
</style>
